// src/hooks/useCollectionProgress.ts
//
// Realtime listener for /users/{uid}/collection_progress. Returns
// the full list sorted by last_find_at desc — newest discoveries
// first. Powers the Collections page and supplies the "you just
// found something" detector hook below.

import { useEffect, useRef, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { CollectionProgress } from '../types/collections';

const tsToDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v === 'number') return new Date(v);
  return null;
};

interface UseCollectionProgressReturn {
  progress: CollectionProgress[];
  isLoading: boolean;
  error: string | null;
}

export const useCollectionProgress = (): UseCollectionProgressReturn => {
  const [progress, setProgress] = useState<CollectionProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    unsubRef.current?.();

    const ref = collection(db, 'users', user.uid, 'collection_progress');
    const q = query(ref, orderBy('last_find_at', 'desc'));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: CollectionProgress[] = [];
        snap.forEach((doc) => {
          const d = doc.data() as any;
          list.push({
            id: doc.id,
            collection_id: d.collection_id || doc.id,
            collection_ref_path: d.collection_ref_path || '',
            collection_kind: d.collection_kind || 'canonical',
            collection_scope: d.collection_scope || 'city',
            collection_title: d.collection_title || '',
            collection_tagline: d.collection_tagline,
            collection_cover_image_url: d.collection_cover_image_url,
            region: d.region,
            found_count: d.found_count || 0,
            total_count: d.total_count || 0,
            hidden_total: !!d.hidden_total,
            first_found_at: tsToDate(d.first_found_at),
            last_find_at: tsToDate(d.last_find_at),
            completed_at: tsToDate(d.completed_at),
            completion_postcard_id: d.completion_postcard_id || null,
            dismissed: !!d.dismissed,
          });
        });
        setProgress(list);
        setIsLoading(false);
      },
      (err) => {
        console.error('collection_progress listener err:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );
    unsubRef.current = unsub;
    return () => unsubRef.current?.();
  }, []);

  return { progress, isLoading, error };
};
